import React, { useRef, useState } from 'react';
import '../styles/LetterHand.css';

function LetterHand({ hand, bank, exchangesAllowed = true, onExchange, onPlaceTile, onReturnTile, selectedLetter, onSelectLetter }) {
  const [touchDrag, setTouchDrag] = useState(null);
  const suppressClickRef = useRef(false);

  const handleDragStart = (e, letter) => {
    if (touchDrag) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('type', 'hand-tile');
    e.dataTransfer.setData('letter', letter);
  };

  const handleExchangeClick = (e, letter) => {
    e.stopPropagation();
    if (exchangesAllowed && bank.length >= 2) {
      onExchange(letter);
    }
  };

  const stopExchangeDrag = (e) => {
    e.stopPropagation();
  };

  const handlePointerDown = (e, letter) => {
    if (e.pointerType === 'mouse') {
      // A browser may omit the synthetic click after a touch gesture. Make
      // sure a later real mouse click is never consumed by that guard.
      suppressClickRef.current = false;
      return;
    }
    if (!e.isPrimary) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setTouchDrag({
      letter,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY
    });
  };

  const handlePointerMove = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;

    e.preventDefault();
    setTouchDrag((drag) => ({
      ...drag,
      x: e.clientX,
      y: e.clientY
    }));
  };

  const finishPointerDrag = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;

    e.preventDefault();
    e.currentTarget.releasePointerCapture?.(e.pointerId);

    const hitTarget = document.elementFromPoint(e.clientX, e.clientY);
    const dropTarget = hitTarget?.closest('[data-grid-cell="true"]');

    const movement = Math.hypot(e.clientX - touchDrag.startX, e.clientY - touchDrag.startY);
    // Touch browsers can dispatch a click after pointerup. This gesture has
    // already been handled, whether it was a tap or a drag.
    suppressClickRef.current = true;

    if (dropTarget && movement > 8) {
      onPlaceTile(
        Number(dropTarget.dataset.row),
        Number(dropTarget.dataset.col),
        touchDrag.letter
      );
    } else if (movement <= 8 || (hitTarget && e.currentTarget.contains(hitTarget))) {
      // A finger can shift within the tile during a tap. It is still a
      // selection when released on the same tile, regardless of distance.
      onSelectLetter?.(touchDrag.letter);
    }

    setTouchDrag(null);
  };

  const cancelPointerDrag = (e) => {
    if (!touchDrag || e.pointerId !== touchDrag.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    suppressClickRef.current = true;
    setTouchDrag(null);
  };

  const handleTileClick = (letter) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onSelectLetter?.(letter);
  };

  const handleTrayDragOver = (e) => {
    if (e.dataTransfer.types.includes('type')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleTrayDrop = (e) => {
    if (e.dataTransfer.getData('type') !== 'grid-tile') return;

    e.preventDefault();
    const row = Number(e.dataTransfer.getData('row'));
    const col = Number(e.dataTransfer.getData('col'));
    if (Number.isFinite(row) && Number.isFinite(col)) {
      onReturnTile?.(row, col);
    }
  };

  const canExchange = exchangesAllowed && bank.length >= 2;

  return (
    <div
      className="letter-hand"
      data-letter-tray="true"
      onDragOver={handleTrayDragOver}
      onDrop={handleTrayDrop}
    >
      <div className="hand-section">
        <h3>Your Letters ({hand.length})</h3>
        <div
          className="letter-tray"
          data-letter-tray="true"
        >
          {hand.map((letter, idx) => (
            <div
              key={idx}
              className={`letter-tile ${selectedLetter === letter ? 'selected' : ''}`}
              draggable={!touchDrag}
              onDragStart={(e) => handleDragStart(e, letter)}
              onClick={() => handleTileClick(letter)}
              onPointerDown={(e) => handlePointerDown(e, letter)}
              onPointerMove={handlePointerMove}
              onPointerUp={finishPointerDrag}
              onPointerCancel={cancelPointerDrag}
            >
              <span className="tile-letter">{letter}</span>
              {exchangesAllowed && <button
                className={`exchange-btn ${!canExchange ? 'disabled' : ''}`}
                onPointerDown={stopExchangeDrag}
                onClick={(e) => handleExchangeClick(e, letter)}
                disabled={!canExchange}
                title={canExchange ? 'Exchange for 2 letters' : 'Need 2 letters in bank'}
              >
                ⇆
              </button>}
            </div>
          ))}
        </div>
        <div className="tray-hint">Tap a tile, then tap a board cell. Drag also works.</div>
      </div>

      {touchDrag && (
        <div
          className="touch-drag-tile"
          style={{
            left: touchDrag.x,
            top: touchDrag.y
          }}
        >
          {touchDrag.letter}
        </div>
      )}
    </div>
  );
}

export default LetterHand;
